"use client";

import { useState, useEffect, useMemo } from "react";
import { useParams } from "next/navigation";
import Link from "next/link";
import {
  ArrowLeft, Calendar, User, Loader2, Clock,
  AlertCircle, BookOpen, ChevronRight,
} from "lucide-react";
import { renderContentToHtml, stripLeadingTitle } from "@/lib/content";
import { highlightAllCodeBlocks, readingTime, scheduleIdle } from "@/lib/highlight";
import { BlogPostingJsonLd, BreadcrumbJsonLd } from "@/components/seo/JsonLd";
import { CodeBlockEnhancer } from "@/components/CodeBlockEnhancer";

const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://platform.monkeyscode.local";

/** JSON:API resource returned by GET /v1/posts/{slug} */
interface PostDetail {
  data: {
    id: number;
    type: "posts";
    attributes: {
      title: string;
      slug: string;
      body: string;
      excerpt: string | null;
      featured_image_url: string | null;
      status: string;
      is_published: boolean;
      published_at: string | null;
      created_at: string;
      updated_at: string;
    };
    relationships: {
      author: { id: number; type: "users"; name: string } | null;
    };
  };
}

function fmtDate(d: string | null): string {
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

function stripHtml(html: string): string {
  return html.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
}

export default function BlogPostPage() {
  const params = useParams();
  const slug = params?.slug as string || "";
  const [post, setPost] = useState<PostDetail["data"] | null>(null);
  const [renderedBody, setRenderedBody] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [imgFit, setImgFit] = useState<"cover" | "contain">("cover");

  useEffect(() => {
    if (!slug) return;
    fetch(`${API_URL}/v1/posts/${encodeURIComponent(slug)}`)
      .then((r) => {
        if (!r.ok) throw new Error("Post not found");
        return r.json();
      })
      .then(async (res: PostDetail) => {
        const p = res.data;
        setPost(p);

        // Render HTML immediately (no CLS), then enhance with syntax highlighting in background
        // Strip leading H1 if it matches the post title (prevents duplicate title)
        const body = stripLeadingTitle(p.attributes.body, p.attributes.title);
        const html = renderContentToHtml(body);
        scheduleIdle(async () => {
          const highlighted = await highlightAllCodeBlocks(html);
          setRenderedBody(highlighted);
        }, { timeout: 5000 });

        // Set SEO metadata dynamically
        const attrs = p.attributes;
        const title = attrs.title ? `${attrs.title} — MonkeysCode Blog` : "MonkeysCode Blog";
        const desc = attrs.body ? stripHtml(attrs.body).slice(0, 160) : attrs.excerpt || "Blog post from MonkeysCode";

        document.title = title;

        const metaEls = new Map<string, HTMLMetaElement>();
        const setMeta = (name: string, content: string) => {
          const attr = name.startsWith("og:") || name.startsWith("twitter:") ? "property" : "name";
          let el = metaEls.get(name);
          if (!el) {
            const selector = `meta[${attr}="${name}"]`;
            const found = document.querySelector<HTMLMetaElement>(selector);
            if (found) {
              el = found;
            } else {
              el = document.createElement("meta");
              el.setAttribute(attr, name);
              document.head.appendChild(el);
            }
            metaEls.set(name, el);
          }
          el.setAttribute("content", content);
        };

        setMeta("description", desc);
        setMeta("og:title", title);
        setMeta("og:description", desc);
        setMeta("og:type", "article");
        setMeta("og:url", `${window.location.origin}/blog/${slug}`);
        if (attrs.featured_image_url) {
          setMeta("og:image", attrs.featured_image_url);
          setMeta("og:image:width", "1200");
          setMeta("og:image:height", "630");
          setMeta("twitter:image", attrs.featured_image_url);
        } else {
          setMeta("og:image", "/og-image.png");
          setMeta("og:image:width", "1200");
          setMeta("og:image:height", "630");
          setMeta("twitter:image", "/og-image.png");
        }
        setMeta("twitter:card", "summary_large_image");
        setMeta("twitter:title", title);
        setMeta("twitter:description", desc);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [slug]);

  const a = post?.attributes;
  const author = post?.relationships?.author;
  const body = a?.body;
  const mins = useMemo(() => (body ? readingTime(body) : 0), [body]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: "var(--mc-violet)" }} />
      </div>
    );
  }

  if (error || !post || !a) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 text-center">
        <AlertCircle className="w-12 h-12 mx-auto mb-4 text-(--text-muted)" />
        <h1 className="text-[24px] font-bold mb-2">Post not found</h1>
        <p className="text-[14px] text-(--text-secondary) mb-6">{error || "This blog post doesn't exist."}</p>
        <Link href="/blog" className="btn btn-ghost">
          <ArrowLeft className="w-4 h-4" /> Back to Blog
        </Link>
      </div>
    );
  }


  return (
    <article className="max-w-4xl mx-auto px-6 py-8">
      <BlogPostingJsonLd
        title={a.title}
        slug={slug}
        excerpt={a.excerpt}
        publishedAt={a.published_at}
        author={author?.name}
      />
      <BreadcrumbJsonLd items={[
        { name: "Home", url: "/" },
        { name: "Blog", url: "/blog" },
        { name: a.title, url: `/blog/${slug}` },
      ]} />
      {/* Breadcrumbs */}
      <nav aria-label="Breadcrumb" className="flex items-center gap-1.5 text-[12px] text-(--text-muted) mb-8">
        <Link href="/blog" aria-label="Back to blog" className="hover:text-(--text-secondary) transition-colors">Blog</Link>
        <ChevronRight className="w-3 h-3" />
        <span className="text-[var(--text-primary)] font-medium truncate">{a.title}</span>
      </nav>

      <div className="max-w-3xl mx-auto">
        {/* Meta row */}
        <div className="flex flex-wrap items-center gap-3 text-[12px] text-(--text-muted) mb-4">
          {author?.name && (
            <span className="flex items-center gap-1.5">
              <User className="w-3.5 h-3.5" />
              {author.name}
            </span>
          )}
          <span className="flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5" />
            {fmtDate(a.published_at || a.created_at)}
          </span>
          <span className="flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5" />
            {mins} min read
          </span>
        </div>

        {/* Featured image — fit mode chosen at runtime based on natural dimensions */}
        {a.featured_image_url && (
          // eslint-disable-next-line @next/next/no-img-element
          <img
            src={a.featured_image_url}
            alt={a.title}
            onLoad={(e) => {
              const img = e.currentTarget;
              const imgRatio = img.naturalWidth / img.naturalHeight;
              const boxRatio = img.clientWidth / img.clientHeight;
              const diff = imgRatio / boxRatio;
              // If ratios differ by more than ~50% in either direction → contain
              setImgFit(diff > 1.5 || diff < 1 / 1.5 ? "contain" : "cover");
            }}
            className={`w-full h-64 lg:h-80 rounded-2xl mb-8 ${
              imgFit === "contain"
                ? "object-contain bg-black/5 dark:bg-white/5"
                : "object-cover"
            }`}
            loading="eager"
            fetchPriority="high"
            width={1200}
            height={630}
          />
        )}

        {/* Title */}
        <h1 className="text-[32px] lg:text-[40px] font-bold tracking-tight leading-[1.1] mb-8">
          {a.title}
        </h1>

        {/* Body — markdown/HTML with syntax highlighting */}
        <CodeBlockEnhancer>
          {renderedBody ? (
            <div
              className="prose prose-sm max-w-none"
              style={{ lineHeight: "1.8", fontSize: "15px" }}
              dangerouslySetInnerHTML={{ __html: renderedBody }}
            />
          ) : (
            <div className="min-h-[40vh]" />
          )}
        </CodeBlockEnhancer>

        {/* Footer */}
        <div className="mt-12 pt-6" style={{ borderTop: "1px solid var(--border)" }}>
          <div className="flex items-center justify-between text-[12px] text-(--text-muted)">
            <span>Last updated: {fmtDate(a.updated_at || a.created_at)}</span>
            <Link
              href="/blog"
              className="flex items-center gap-1.5 hover:text-(--text-secondary) transition-colors"
            >
              <BookOpen className="w-3.5 h-3.5" />
              Back to all posts
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}
